import { LuTrendingDown, LuTrendingUp } from 'react-icons/lu';
import { FormattedMessage } from 'react-intl';

import { ChartRendererProps } from '../../types/chart-renderer-props';
import { toPeriodComparisonMetric } from '../../utils/to-period-comparison-metric';

const trendClassName = (direction: 'up' | 'down' | 'flat'): string => {
    if (direction === 'up') {
        return 'text-status-success';
    }

    if (direction === 'down') {
        return 'text-status-warning';
    }

    return 'text-brand-muted';
};

/** Metric card for percentage values (e.g. IoT uptime). Uses absolute point change between the last two bins. */
export const PercentMetricChart = ({ data, className }: ChartRendererProps) => {
    const metric = toPeriodComparisonMetric(data, 'absolute');

    if (!metric) {
        return null;
    }

    return (
        <div className={`flex w-full items-end justify-between gap-3 ${className ?? ''}`}>
            <div className='flex min-w-0 flex-col items-start justify-end gap-0.5'>
                <div className='text-foreground-default justify-start text-xl font-semibold'>
                    <FormattedMessage
                        id='chart.components.renderers.percent-metric-chart.value'
                        description='Percentage value shown on a percent metric chart card'
                        defaultMessage='{value}%'
                        values={{ value: Math.round(metric.value) }}
                    />
                </div>
            </div>
            {metric.percentChange !== null && (
                <div className={`flex shrink-0 items-center gap-1 text-base font-semibold ${trendClassName(metric.direction)}`}>
                    {metric.direction === 'down' && <LuTrendingDown className='size-5' />}
                    {metric.direction === 'up' && <LuTrendingUp className='size-5' />}
                    <FormattedMessage
                        id='chart.components.renderers.percent-metric-chart.point-change'
                        description='Absolute percentage-point change shown next to a percent metric chart value'
                        defaultMessage='{value} pt'
                        values={{ value: metric.percentChange }}
                    />
                </div>
            )}
        </div>
    );
};
